import React from 'react';
import { Alert, Box, Button, LinearProgress, Link, Stack, Typography } from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DownloadIcon from '@mui/icons-material/Download';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { CampaignAsset } from '../../../../support/campaigns';
import {
  CampaignAssetStorageUnavailableError,
  getCampaignAssetDownload,
  uploadCampaignAsset,
} from '../../../../support/campaignAssets';

// Human-readable byte size that scales past KB — a 455 MiB deck should read "455 MB", not
// "466236 KB". Whole B/KB read cleaner without a decimal; MB and up keep one.
const humanSize = (bytes: number | null | undefined): string => {
  if (!bytes || bytes <= 0) return '';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let n = bytes;
  let i = 0;
  while (n >= 1024 && i < units.length - 1) {
    n /= 1024;
    i += 1;
  }
  const value = i <= 1 ? Math.round(n) : Math.round(n * 10) / 10;
  return `${value} ${units[i]}`;
};

// Common upload MIME types → a short, friendly format label. Used only as a fallback when the object
// key has no usable extension (the extension is preferred since it is always present on the key).
const MIME_FORMAT: Record<string, string> = {
  'application/pdf': 'PDF',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'PPTX',
  'application/vnd.ms-powerpoint': 'PPT',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'DOCX',
  'application/msword': 'DOC',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'XLSX',
  'application/vnd.ms-excel': 'XLS',
  'image/png': 'PNG',
  'image/jpeg': 'JPG',
  'image/gif': 'GIF',
  'image/svg+xml': 'SVG',
  'text/plain': 'TXT',
};

// A friendly format label for the file — the uppercased extension off the object key (which always
// carries one, e.g. `…/uuid.pptx` → "PPTX"), else the content type, else a generic "FILE".
const fileFormat = (row: CampaignAsset): string => {
  const key = row.object_key || '';
  const dot = key.lastIndexOf('.');
  const ext = dot >= 0 ? key.slice(dot + 1).trim() : '';
  if (ext && ext.length <= 5) return ext.toUpperCase();
  const ct = (row.content_type || '').toLowerCase();
  return MIME_FORMAT[ct] ?? 'FILE';
};

// A human-friendly name for the uploaded file. The object key's basename is a storage hash, so prefer
// the asset's authored title; fall back to a format-derived label rather than exposing the hash path.
const friendlyName = (row: CampaignAsset): string => {
  const title = row.title?.trim();
  if (title) return title;
  const fmt = fileFormat(row);
  return fmt !== 'FILE' ? `${fmt} file` : 'Uploaded file';
};

/**
 * The file half of a campaign asset row: shows the current object key / type / size, uploads (or
 * replaces) the bytes through the campaign-asset presign flow, and offers a signed-GET download.
 *
 * The upload is the same three-step direct-to-S3 flow the report-figure uploader uses (presign ->
 * browser PUT -> persist the returned `object_key`), widened to arbitrary files. On success it calls
 * `onUploaded` with the fields to persist on the row; the caller's `save` does the full-record PUT.
 *
 * Extracted from `AssetsEditor` so both the campaign-level Assets editor and the per-step Files
 * section can manage an asset's bytes in place with one widget.
 *
 * File management here is self-service: drag a file onto the row (or click Upload/Replace) to send
 * the bytes, and Delete the file to clear the object off the row (the asset row itself stays — use
 * the Assets editor / step "remove reference" to drop the whole asset). Drag-drop mirrors the
 * journey-map `JourneyMapFileDropper` (native HTML5 drag events, upload-or-replace on drop).
 */
export function AssetFileField(props: {
  row: CampaignAsset;
  campaignId: number;
  canWrite: boolean;
  onUploaded: (next: Partial<CampaignAsset>) => void;
}): JSX.Element {
  const { row, campaignId, canWrite, onUploaded } = props;
  const inputRef = React.useRef<HTMLInputElement | null>(null);
  const [progress, setProgress] = React.useState<number | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [downloading, setDownloading] = React.useState(false);
  const [dragActive, setDragActive] = React.useState(false);

  const busy = progress !== null;
  const hasFile = !!row.object_key;

  const pick = () => {
    if (!canWrite || busy) return;
    inputRef.current?.click();
  };

  // Upload/replace the row's bytes. Shared by the file picker and the drag-drop handler.
  const handleFile = async (file: File | null | undefined) => {
    if (!file || !canWrite || busy) return;

    setError(null);
    setProgress(0);
    try {
      const up = await uploadCampaignAsset(
        campaignId,
        file,
        { kind: row.kind, key: row.key },
        { onProgress: (p) => setProgress(p.percent) },
      );
      onUploaded({
        object_key: up.object_key,
        content_type: up.content_type,
        size_bytes: up.size_bytes,
      });
    } catch (e) {
      setError(
        e instanceof CampaignAssetStorageUnavailableError
          ? 'File storage is not configured for this environment yet.'
          : (e as Error)?.message ?? 'Upload failed.',
      );
    } finally {
      setProgress(null);
    }
  };

  const onFile = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    // Reset the input so re-picking the same file fires onChange again.
    event.target.value = '';
    void handleFile(file);
  };

  const onDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragActive(false);
    if (!canWrite || busy) return;
    void handleFile(event.dataTransfer?.files?.[0]);
  };

  // Clear the file off the row (full-record PUT with the bytes-fields blanked). The asset row
  // itself is untouched — reusing `onUploaded` keeps the caller's single save path.
  const removeFile = () => {
    if (!canWrite || busy) return;
    setError(null);
    onUploaded({ object_key: '', content_type: '', size_bytes: null });
  };

  const download = async () => {
    if (downloading) return;
    setError(null);
    setDownloading(true);
    try {
      const { url } = await getCampaignAssetDownload(row.id);
      if (url) window.open(url, '_blank', 'noopener,noreferrer');
    } catch (e) {
      setError((e as Error)?.message ?? 'Could not create a download link.');
    } finally {
      setDownloading(false);
    }
  };

  // Wrap the whole widget so a file can be dropped anywhere on it. Guarded by `canWrite` so a
  // read-only viewer never shows the drag affordance.
  const dropHandlers = canWrite
    ? {
        onDragOver: (e: React.DragEvent<HTMLDivElement>) => {
          e.preventDefault();
          setDragActive(true);
        },
        onDragLeave: () => setDragActive(false),
        onDrop,
      }
    : {};

  return (
    <Stack
      spacing={0.5}
      {...dropHandlers}
      sx={
        canWrite
          ? {
              p: 0.5,
              borderRadius: 1,
              border: '1px dashed',
              borderColor: dragActive ? 'primary.main' : 'transparent',
              backgroundColor: dragActive ? 'action.hover' : 'transparent',
              transition: 'background-color 120ms, border-color 120ms',
            }
          : undefined
      }
    >
      <Alert severity={hasFile ? 'success' : 'info'} icon={false} sx={{ py: 0.5 }}>
        <strong>File:</strong>{' '}
        {hasFile ? (
          <>
            {friendlyName(row)}
            {` · ${fileFormat(row)}`}
            {row.size_bytes ? ` · ${humanSize(row.size_bytes)}` : ''}
            {' · '}
            <Link
              component="button"
              type="button"
              variant="body2"
              disabled={downloading}
              onClick={download}
            >
              <DownloadIcon fontSize="inherit" sx={{ verticalAlign: 'middle', mr: 0.25 }} />
              Download
            </Link>
          </>
        ) : (
          'not uploaded'
        )}
      </Alert>

      {canWrite && (
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <input ref={inputRef} type="file" hidden onChange={onFile} />
          <Button
            size="small"
            variant="outlined"
            startIcon={<UploadFileIcon fontSize="small" />}
            disabled={busy}
            onClick={pick}
          >
            {busy ? 'Uploading…' : hasFile ? 'Replace file' : 'Upload file'}
          </Button>
          {hasFile && (
            <Button
              size="small"
              color="error"
              variant="outlined"
              startIcon={<DeleteOutlineIcon fontSize="small" />}
              disabled={busy}
              onClick={removeFile}
            >
              Delete file
            </Button>
          )}
          {!hasFile && !busy && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
            >
              <CloudUploadOutlinedIcon fontSize="inherit" /> or drop a file here
            </Typography>
          )}
          {busy && (
            <Box sx={{ flex: 1, minWidth: 120 }}>
              <LinearProgress variant="determinate" value={progress ?? 0} />
            </Box>
          )}
        </Stack>
      )}

      {error && (
        <Typography variant="caption" color="error">
          {error}
        </Typography>
      )}
    </Stack>
  );
}

export default AssetFileField;
